<template>
    <v-date-picker
        ref="calendar"
        v-model="valueComputed"
        :min-date="minDateComputed"
        :max-date="maxDate"
        :disabled-dates="{ days: disabledDatesComputed }"
        :masks="masks"
        :attributes="attributes"
        :available-dates="{
            start: valueComputed || minDateComputed,
            end: valueComputed || minDateComputed
        }"
        :is-range="isRange"
        locale="pt-BR"
        mode="date"
        title-position="left"
        transition="fade"
        :is-required="isRequired"
        :popover="{ positionFixed: positionFixed }"
    >
        <template v-if="!dayMode" #default="{ togglePopover }">
            <label
                class="ui-input"
                :class="[
                    size,
                    { 'ui-input-group': icon },
                ]"
            >
                <!-- Label shown in the layout, being optional -->
                <span v-if="label" class="label">
                    <span v-html="label"/>
                    <span v-if="required" class="label-required">*</span>
                </span>

                <button
                    v-if="icon && groupReverse"
                    class="group-reverse"
                    :data-ui="dataUiButton"
                    type="button" :class="{ 'button-error': setError }"
                    @click="togglePopover"
                >
                    <i class="fal fa-calendar-alt" :class="{ 'icon-danger': setError }"/>
                </button>

                <!-- Input mostrado no layout -->
                <input
                    ref="input"
                    :value="displayedDate"
                    :class="[
                        status,
                        { 'input-error': setError },
                        { 'ui-input-reverse': groupReverse },
                        { 'disabled-input': isDisabled }
                    ]"
                    :data-ui="dataUiInput || null"
                    class="form-date input"
                    type="date"
                    @input="onInput"
                    @focus="onFocus"
                    @blur="onBlur"
                />
                <!-- Here is the icon / label of the input, being optional -->
                <button
                    v-if="icon && !groupReverse" class="group"
                    :data-ui="dataUiButton"
                    type="button" :class="{ 'button-error': setError }"
                    @click="togglePopover"
                >
                    <i class="fal fa-calendar-alt" :class="{ 'icon-danger': setError }" />
                </button>

                <div v-if="groupReverse" class="button-date-picker">
                    <ui-button
                        v-if="icon"
                        type="button"
                        design="primary md brl-off"
                        @click="onClickGroupReverseApply"
                    >
                        Enviar
                    </ui-button>
                </div>

                <span v-if="setError" class="error-icon">
                    <i class="fal fa-times"/>
                </span>

                <div v-if="setError" class="observation-message" :class="{ 'error': setError }">
                    {{ observation }}
                </div>
            </label>
        </template>

        <!-- dayMode -->
        <template v-else #default="{ togglePopover }">
            <label
                class="ui-input"
                :class="[
                    size,
                    {
                        'ui-input-group': icon,
                        'input-error': setError
                    }
                ]"
            >
                <!-- Label shown in the layout, being optional -->
                <span v-if="label" class="label">
                    <span v-html="label"/>
                    <span v-if="required" class="label-required">*</span>
                </span>

                <!-- Input mostrado no layout -->
                <input
                    ref="input"
                    v-model="daysComputed"
                    v-number
                    :class="[status, { 'input-error': setError }]"
                    :data-ui="dataUiInput || null"
                    class="form-date input"
                    type="tel"
                    :disabled="disabled"
                    @focus="onFocus"
                    @blur="onBlur"
                />

                <!-- Here is the icon / label of the input, being optional -->
                <button
                    v-if="icon"
                    class="group"
                    type="button"
                    :class="{ 'button-error': setError }"
                    :data-ui="dataUiButton"
                    @click="togglePopover"
                >
                    <i class="fal fa-calendar-alt" :class="{ 'icon-danger': setError }"/>
                </button>

                <span v-if="setError" class="error-icon">
                    <i class="fal fa-times"/>
                </span>
            </label>

            <div v-if="setError" class="observation-message" :class="{ 'error': setError }">
                {{ observation }}
            </div>
        </template>
    </v-date-picker>
</template>

<script>
    import { uiButton } from '@/components/ui/buttons'
import { number } from '@/directives'
import vDatePicker from 'v-calendar/lib/components/date-picker.umd'
import { mask } from 'vue-the-mask'

    const moment = require('moment')
    require('moment/locale/pt-br')

    export default {
        name: 'ui-datepicker',

        components: {
            vDatePicker,
            uiButton
        },

        directives: {
            mask,
            number,
        },

        props: {
            /**
                * This property defines the value passed to the
                * datepicker component
                */
            value: {
                required: false,
                default () {
                if (this.isRange) {
                    return {
                        start: null,
                        end: null
                    };
                } else {
                    return moment(new Date()).format('YYYY-MM-DD');
                }
}            },
            /**
                * This property defines the label presented together
                * with input / datepicker
                */
            label: {
                type: String,
                required: false,
                default: ''
            },

            /**
                * Use to change the size of the input
                */
            size: {
                type: String,
                required: false,
                default: 'md'
            },

            /**
                * This property defines whether the input will have the icon
                * calendar added to the component's visual
                */
            icon: {
                type: Boolean,
                required: false,
                default: true
            },

            /**
                * This property defines the date of selection
                * date to the current day
                */
            minDate: {
                type: [Date, Boolean],
                required: false,
                default () {
                    return new Date()
                }
            },
            maxDate: {
                type: [Date, Boolean],
                required: false,
                default () {
                    return null
                }
            },

            /**
                * This property allows the user to
                * write in the field (input) and / or select a
                * date through the datepicker component when clicking
                * on the button / label with the icon.
                */
            dayMode: {
                type: Boolean,
                required: false,
                default: false
            },

            /**
                * Define the input as required in html5 and apply the '*' operator to the label
                */
            required: {
                type: Boolean,
                required: false,
                default: false
            },

            /**
                * Define what days are available to select
                */
            availableDates: {
                type: Array,
                required: false,
                default() {
                    return []
                }
            },
            /**
                * This property must be used when the component can't be filled with a date
                * before the current date
                */
            context: {
                type: String,
                required: false,
                default: ''
            },
            /**
                * mostra uma informação abaixo do select
                */
            observation: {
                type: String,
                default: ''
            },
            error: {
                type: Boolean,
                required: false,
                default: false
            },
            /**
                * Define uma tag de teste para os testes integrados do input
                */
            dataUiInput: {
                type: String,
                required: false,
                default: ''
            },
            /**
                * Define uma tag de teste para os testes integrados do botão
                */
            dataUiButton: {
                type: String,
                required: false,
                default: ''
            },
            /**
            * Define o calendario a esquerda
            */
            groupReverse: {
                type: Boolean,
                default: false
            },
            /**
            * Define o calendario como range
            */
            isRange: {
                type: Boolean,
                default: false
            },
            /**
            * Desabilita o input
            */
            disabled: {
                type: Boolean,
                required: false,
                default: false
            },
            onClickGroupReverse: {
                type: Function,
				required: false,
                default: () => {}
			},
            isRequired:{
				default: () => {}
			},
            positionFixed: {
                type: Boolean,
                required: false,
                default: false
            },
            isDisabled: {
                type: Boolean,
                required: false,
                default: true
            },
        },

        data() {
            return {
                masks: {
                    input: 'DD/MM/YYYY',
                },
                dataDigited: '',
                attributes: [{
                    highlight: {
                        base: {
                            fillMode: 'light'
                        },
                    },
                    dates: this.valueComputed,
                }],
                status: null,
                days: this.value,
                dataTimeout: null,
                setError: this.error,
                daysValue: this.isRange ? {
                    start: this.value && this.value.start ? this.value.start : null,
                    end: this.value && this.value.end ? this.value.end : null
                } : null,
            }
        },

        computed: {
            daysComputed: {
                get() {
                    return this.days
                },

                set(value) {
                    let date = new Date()
                    date.setDate(date.getDate() + parseInt(value))
                    this.valueComputed = date
                }
            },
            displayedDate() {
                if (this.isRange && this.valueComputed) {
                    const format = 'YYYY-MM-DD';
                    return this.valueComputed.end ? moment(this.valueComputed.end).format(format) : '';
                } else if (this.valueComputed) {
                    return moment(this.valueComputed).format('YYYY-MM-DD');
                } else {
                    return '';
                }
            },

            valueComputed: {
                get() {
                    if (this.isRange) {
                        return {
                            start: this.daysValue.start,
                            end: this.daysValue.end
                        };
                    }
                    let date = null
                    if (this.dayMode) {
                        date = new Date()
                        date.setDate(date.getDate() + parseInt(this.value))

                    } else if (this.value && this.value.includes('-')) {
                        date = new Date(this.value.replace('-', '/'))
                    }

                    return date
                },

                set(value) {
                    if (this.isRange) {
                        this.daysValue.start = value.start;
                        this.daysValue.end = value.end;
                        if (!this.areDatesEqual(this.value, value)) {
                            this.$emit('input', value);
                        }
                    }

                    if (this.dayMode) {
                        let now = new Date();
                        now.setHours(0, 0, 0, 0);
                        let valueDate = this.handleDateInput(value);


                        if (isNaN(valueDate)) return;

                        valueDate.setHours(0, 0, 0, 0);
                        const diffTime = Math.abs(now - valueDate.getTime());
                        const diffDays = diffTime ? Math.ceil(diffTime / (1000 * 60 * 60 * 24)) : 0;
                        this.days = diffDays;
                        this.dataDigited = moment(valueDate).format('YYYY-MM-DD');

                        if (this.value !== this.days) {
                            this.$emit('input', this.days);
                        }
                    } else {
                        value = moment(value?.end ? value?.end : value).format('YYYY-MM-DD')
                        const formattedValue = moment(value).format('YYYY-MM-DD');
                        this.dataDigited = value
                        if (this.value !== formattedValue) {
                            this.$emit('input', formattedValue);
                        }
                    }
                }
            },
            /**
                * Computed property que define quais os dias não podem ser selecionados levando em consideração
                * os dias permitidos, caso existam
                */
            disabledDatesComputed() {
                let dates = []
                if (this.availableDates.length) {
                    dates = [...Array(31).keys()].map(i => i + 1).filter(i => !this.availableDates.includes(i))
                }

                return dates
            },

            /**
                * Computed property que define a data mínima a ser selecionada no calendário levando com consideração
                * os dias permitidos, caso existam
                */
            minDateComputed() {
                // Verifica se a data mínima está desativada
                if (typeof this.minDate === 'boolean' && !this.minDate) return null

                let date = new Date(this.minDate.getTime())
                if (this.availableDates.length) {
                    let availableDate = this.availableDates.map(day => parseInt(day))
                        .filter(day => parseInt(day) >= date.getDate())
                        .sort((a, b) => a - b)
                        .find(day => day)
                    /** Caso não existam dias disponíveis no mês atual, altera-se a data mínima para o próximo mês */
                    if (!availableDate) {
                        availableDate = this.availableDates.map(day => parseInt(day))
                            .sort((a, b) => b - a)
                            .find(day => day)
                    }

                    date.setDate(availableDate)
                }

                return date
            }
        },

        watch: {
            value: {
                immediate: true,
                handler(newVal) {
                if (this.isRange) {
                    if (!this.areDatesEqual(this.valueComputed, newVal)) {
                        this.valueComputed = { start: newVal.start || null, end: newVal.end || null };
                    }
                } else {
                    if (this.valueComputed !== newVal) {
                        this.valueComputed = newVal;
                    }
                }
                }
            },
            valueComputed: {
                handler(newVal) {
                    if (this.isRange) {
                        this.$emit('input', newVal);
                    }
                },
                deep: true
            },
            error: {
                immediate: true, // Chama a função de callback imediatamente após o componente ser montado
                handler(newValue) {
                    this.setError = newValue
                },
            }
        },

        methods: {
            /**
             * Trata as datas do valueComputed do input datePicker
             */
            handleDateInput(value){
                let valueDate;
                if (value instanceof Date) {
                    valueDate = new Date(value.getTime());
                    return valueDate
                }
                if (typeof value === 'number' || !isNaN(parseInt(value))) {
                    valueDate = new Date();
                    valueDate.setDate(valueDate.getDate() + parseInt(value));
                    return valueDate
                }
                return new Date(value);
            },
            areDatesEqual(date1, date2) {
                return moment(date1).isSame(moment(date2), 'day');
            },
            /**
             *
             * metodo que captura o evento
             */
            onInput(event) {
                this.dataDigited = event.target.value;
                this.transformDate(this.dataDigited, this.context);
            },

            onInputChange(event) {
                const inputValue = event.target.value;
                if (this.isRange) {
                    const dates = inputValue.split(' - ');
                    const format = 'DD/MM/YYYY';
                    if (dates.length === 2) {
                        const startDate = moment(dates[0], format).toDate();
                        const endDate = moment(dates[1], format).toDate();
                        if (startDate && endDate) {
                            this.valueComputed = { start: startDate, end: endDate };
                        }
                    } else if (dates.length === 1) {
                        const startDate = moment(dates[0], format).toDate();
                        if (startDate) {
                            this.valueComputed = { start: startDate, end: null };
                        }
                    }
                } else {
                    const date = moment(inputValue, 'DD/MM/YYYY').toDate();
                    if (date) {
                        this.valueComputed = date;
                    }
                }
            },
            returnDataPtBr(val) {
                if (val) {
                    let setSplit = val.split('-', 3)
                    return `${setSplit[2]}/${setSplit[1]}/${setSplit[0]}`
                }
            },
            transformDate(date, context) {

                clearTimeout(this.dataTimeout)
                this.dataTimeout = setTimeout(() => {
                    if(moment(date).isBefore(moment()) && context === 'today' || moment(date).isAfter(moment()) && context === 'notFuture'){
                        this.dataDigited = new Date()
                    }
                    this.valueComputed = this.dataDigited
                }, 1500)

            },
            onFocus(event) {
                this.setError = false
                this.$emit('focus', event.target.value)
            },
            onBlur(event){
                if (event.target.value == '') {
                    this.setError = this.error
                }
                this.$emit('blur', event.target.value)
            },
            iconTogglePopover() {
                this.onBlur()
            },
            onClickGroupReverseApply() {
                if(this.onClickGroupReverse){
                    this.onClickGroupReverse()
                }
            }
        }
    }
</script>

<style lang="stylus" scoped>
    @import './styles.styl'
</style>